import { useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ProductFormData, ProductFormState } from "@/components/screens/ProductFormPage/types";
import useToast from "@/contexts/toast-context";
import useCreateProductImage from "@/hooks/requests/product-images/use-create-product-image";
import useGetProductImages from "@/hooks/requests/product-images/use-get-product-images";
import { buildImageFormData } from "@/components/templates/ProductForm/form-data";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";
import useCreateProduct from "@/hooks/requests/product/use-create-product";
import useUpdateProduct from "@/hooks/requests/product/use-update-product";
import { IProductImage as ProductImageInterface } from "@/interfaces/product-image.interface";
import { ImageData } from "@/interfaces/product.interface";
import { FilterType } from "@/types/filter.types";
import { buildErrorMessage } from "@/helpers/error-helpers";
import { normalizeVariationsToBackend } from "@/helpers/variation-helpers";

type Props = {
  messages: ProductFormPageMessages;
  customerId: number;
  isEditing: boolean;
};

export const useProductFormState = ({ messages, customerId, isEditing }: Props) => {
  const { values, setFieldValue, dirty } = useFormikContext<ProductFormData>();
  const { showToast } = useToast();
  const { create: createProduct, isLoading: isCreatingProduct } = useCreateProduct();
  const { update: updateProduct, isLoading: isUpdatingProduct } = useUpdateProduct();
  const { create: createImage, isLoading: isUploadingImage } = useCreateProductImage();

  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);

  const [state, setState] = useState<ProductFormState>({
    productId: values.id || null,
    isProductSaved: !!values.id,
    hasUnsavedChanges: false,
    isUploadingImages: false,
    uploadProgress: { current: 0, total: 0 },
  });

  // Ref para rastrear imagens já enviadas (previne re-upload)
  const uploadedImageNamesRef = useRef<Set<string>>(new Set());

  // Usar values.id diretamente para evitar problemas de closure
  const currentProductId = values.id || state.productId;

  const { data: productImagesData, mutate: mutateImages } = useGetProductImages({
    filters: currentProductId
      ? [
          {
            property: "productId",
            filterType: FilterType.Equals,
            value: currentProductId,
          },
        ]
      : [],
    pageNumber: 1,
    pageSize: 100,
    enabled: !!currentProductId,
  });

  // Memoizar savedImages para evitar re-renderizações desnecessárias
  const savedImages = useMemo(() => {
    const rawImages = productImagesData?.items || [];

    const uniqueImages = Array.from(new Map(rawImages.map((img) => [img.id, img])).values());

    // Filtrar imagens marcadas para exclusão
    const imagesToDeleteSet = new Set(imagesToDelete?.map(String) || []);

    const filteredImages = uniqueImages.filter((img) => !imagesToDeleteSet.has(String(img.id)));

    return filteredImages;
  }, [productImagesData?.items, imagesToDelete]);

  // Wrapper para reloadImages que força revalidação e aguarda
  const reloadImages = useCallback(async () => {
    // Usar values.id diretamente para evitar problemas de closure
    const currentProductId = values.id || state.productId;

    if (!currentProductId) {
      return;
    }

    try {
      // Atualizar o estado primeiro para garantir que o hook use o ID correto
      if (currentProductId !== state.productId) {
        setState((prev) => ({
          ...prev,
          productId: currentProductId,
        }));

        // Aguardar um tick para o React processar a atualização
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Forçar revalidação do SWR
      await mutateImages();
    } catch (error) {
      // Erro ao recarregar imagens - silencioso
    }
  }, [values.id, state.productId, mutateImages, productImagesData]);

  // Atualizar productId quando valores mudarem
  useEffect(() => {
    if (values.id && values.id !== state.productId) {
      setState((prev) => ({
        ...prev,
        productId: values.id,
        isProductSaved: true,
      }));

      // Forçar revalidação das imagens quando o produto muda
      mutateImages();
    }
  }, [values.id, state.productId, mutateImages]);

  // Monitorar mudanças não salvas
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      hasUnsavedChanges: dirty && state.isProductSaved,
    }));
  }, [dirty, state.isProductSaved]);

  // Salvar produto (criar ou atualizar)
  const saveProduct = useCallback(
    async (showSuccessMessage = false): Promise<number | null> => {
      try {
        // Remover campos que não fazem parte de IProductInput
        const { 
          productImages, 
          skus, 
          skusToDelete, 
          isEditing, 
          isEditingSku, 
          sku, 
          selectedCategories,
          ...productData 
        } = values;

        if (!state.productId) {
          // ❌ Criar produto: validar campos obrigatórios
          if (!productData.code || !productData.title) {
            showToast({
              text: messages.productMustHaveCodeAndTitle,
              type: "error",
            });
            return null;
          }

          const response = await createProduct({
            formData: {
              ...productData,
              customerId,
              productImages: null,
              skus: [],
              variations: normalizeVariationsToBackend(productData.variations || []),
            },
            successMessage: showSuccessMessage ? messages.productSaveSuccess : "",
            errorMessage: messages.productSaveError,
          });

          if (response) {
            setFieldValue("id", response.id);
            setState((prev) => ({
              ...prev,
              productId: response.id,
              isProductSaved: true,
              hasUnsavedChanges: false,
            }));
            return response.id;
          }
        } else {
          // ✅ Atualizar produto existente: incluir código (obrigatório no PUT, mas não pode ser alterado)
          // Remover apenas campos que não fazem parte de IProductInput
          const { customerId: _, ...productDataWithoutCustomerId } = productData;
          await updateProduct({
            _id: state.productId,
            formData: {
              ...productDataWithoutCustomerId,
              code: productData.code || "", // Obrigatório no PUT (deve ser o código original)
              brand: productData.brand || "", // Obrigatório (não nullable no modelo)
              ncm: productData.ncm || "", // Obrigatório (não nullable no modelo)
              customerId, // Garantir customerId correto do hook
              productImages: null,
              skus: [],
              variations: normalizeVariationsToBackend(productData.variations || []),
            },
            successMessage: showSuccessMessage ? messages.productSaveSuccess : "",
            errorMessage: messages.productSaveError,
          });

          setState((prev) => ({
            ...prev,
            hasUnsavedChanges: false,
          }));
          return state.productId;
        }
      } catch (error) {
        // Usar buildErrorMessage para extrair mensagens específicas da API
        const errorMessage = buildErrorMessage(error, messages.productSaveError);

        showToast({
          text: errorMessage,
          type: "error",
        });
        return null;
      }

      return null;
    },
    [
      values,
      state.productId,
      createProduct,
      updateProduct,
      customerId,
      messages,
      setFieldValue,
      showToast,
    ]
  );

  const uploadImages = useCallback(
    async (images: any[], productId: number): Promise<void> => {
      // Filtrar imagens que realmente precisam ser enviadas
      const imagesToUpload = images.filter((img) => {
        const hasFile = "file" in img;
        const hasId = img.id;
        const notUploaded = !uploadedImageNamesRef.current.has(img.name);

        return hasFile && !hasId && notUploaded;
      });

      if (imagesToUpload.length === 0) {
        return;
      }

      setState((prev) => ({
        ...prev,
        isUploadingImages: true,
        uploadProgress: { current: 0, total: imagesToUpload.length },
      }));

      try {
        const uploadedImages: any[] = [];

        for (let i = 0; i < imagesToUpload.length; i++) {
          const image = imagesToUpload[i] as ImageData;
          const imageFormData = buildImageFormData(image, productId.toString());

          const result = await createImage({
            formData: imageFormData,
            errorMessage: messages.imageCreateError,
          });

          // Adicionar o resultado com ID à lista de imagens enviadas
          if (result) {
            uploadedImages.push(result);
            // Marcar como enviada na ref
            uploadedImageNamesRef.current.add(image.name);
          }

          setState((prev) => ({
            ...prev,
            uploadProgress: { current: i + 1, total: imagesToUpload.length },
          }));
        }

        // Atualizar Formik com os IDs das imagens enviadas
        if (uploadedImages.length > 0) {
          const currentImages = values.productImages || [];

          const updatedImages = currentImages.map((img) => {
            // Se a imagem tem file mas não tem id, procurar nos uploads bem-sucedidos
            if ("file" in img && !img.id) {
              const uploaded = uploadedImages.find((u) => u.name === img.name);
              if (uploaded) {
                // Remover 'file' para marcar como não-pendente
                const { file, ...rest } = img as any;
                return { ...rest, id: uploaded.id, phisicalAddress: uploaded.phisicalAddress };
              }
            }
            return img;
          });

          // ✅ Aguardar atualização do Formik
          await new Promise<void>((resolve) => {
            setFieldValue("productImages", updatedImages);
            // Dar tempo para Formik sincronizar
            setTimeout(() => resolve(), 100);
          });
        }

        showToast({
          text: messages.imageUploadSuccess,
          type: "success",
        });

        // Recarregar imagens
        await mutateImages();
      } catch (error) {
        showToast({
          text: messages.imageUploadError,
          type: "error",
        });
      } finally {
        setState((prev) => ({
          ...prev,
          isUploadingImages: false,
          uploadProgress: { current: 0, total: 0 },
        }));
      }
    },
    [createImage, messages, showToast, mutateImages, values.productImages, setFieldValue]
  );

  // Garantir que produto esteja salvo antes de ação
  const ensureProductSaved = useCallback(async (): Promise<boolean> => {
    // ✅ Se produto já tem ID, considerar como salvo
    if (state.productId) {
      // CRITICAL: Verificar se há imagens pendentes de upload
      const pendingImages =
        values.productImages?.filter((img) => {
          const hasFile = "file" in img;
          const hasId = "id" in img && img.id;
          const notYetUploaded = !uploadedImageNamesRef.current.has(img.name);

          return hasFile && !hasId && notYetUploaded;
        }) || [];

      if (pendingImages.length > 0) {
        await uploadImages(pendingImages, state.productId);

        // Aguardar processamento das imagens no backend
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Recarregar lista de imagens com revalidação forçada
        await mutateImages();
      }

      return true; // ✅ Sempre permitir se produto tem ID
    }

    // 🆕 Se produto não tem ID, salvar automaticamente SEM mensagem de sucesso
    const productId = await saveProduct(false);
    
    if (productId) {
      // Aguardar para garantir que o produto foi salvo
      await new Promise((resolve) => setTimeout(resolve, 300));
      return true;
    }

    return false;
  }, [
    state.isProductSaved,
    state.hasUnsavedChanges,
    state.productId,
    values.code,
    values.title,
    values.productImages,
    saveProduct,
    uploadImages,
    mutateImages,
    showToast,
    messages,
  ]);

  // Verificar se pode abrir modal de SKU
  const canOpenSkuModal = useCallback((): boolean => {
    return state.isProductSaved && !state.isUploadingImages;
  }, [state.isProductSaved, state.isUploadingImages]);

  // Obter mensagem de tooltip quando botão está desabilitado
  const getDisabledTooltip = useCallback((): string => {
    if (!state.isProductSaved) {
      return messages.productMustBeSavedFirst;
    }
    if (state.isUploadingImages) {
      return messages.waitForImageUpload;
    }
    return "";
  }, [state.isProductSaved, state.isUploadingImages, messages]);

  // Salvamento automático removido - CREATE será apenas quando:
  // 1. Clicar no botão "Salvar" (FormWrapper.handleSubmit)
  // 2. Clicar em "Criar SKU" (ensureProductSaved via handleOpenDialog)
  // A partir do primeiro salvamento, todas as operações serão PUT

  return {
    state,
    saveProduct,
    uploadImages,
    ensureProductSaved,
    canOpenSkuModal,
    getDisabledTooltip,
    isLoading: isCreatingProduct || isUpdatingProduct || isUploadingImage,
    savedImages,
    reloadImages,
    imagesToDelete,
    setImagesToDelete,
  };
};
